import React, { useMemo, useState } from "react";
import { Search, Filter as FilterIcon, X } from "lucide-react";

/**
 * Shared list primitive used by RfqList, SupplierInbox, Orders, etc.
 *
 * Goals:
 *   - One pattern for search + filter chips + empty state + responsive table.
 *   - No business logic — pure presentational + filtering helpers.
 *   - Mobile-first: piggybacks on the existing `table.data` card-stacker.
 *   - Uncontrolled by default (own its own `q` + `filter` state); the parent
 *     can opt in to control by passing `filterValue` / `onFilterChange` etc.
 *
 * Usage:
 *   <DataList
 *     testId="rfq-list"
 *     rows={rows}
 *     columns={[
 *       { key: "code",        label: "Code",     render: r => <Mono>{r.code}</Mono>, search: r => r.code },
 *       { key: "product",     label: "Product",  render: r => r.product_name,        search: r => r.product_name },
 *       ...
 *     ]}
 *     filters={[
 *       { label: "Status", key: "status",
 *         options: [{ value: "all", label: "All" }, { value: "open", label: "Open" }, ...],
 *         match: (row, value) => value === "all" || row.status === value },
 *     ]}
 *     emptyText="No RFQs."
 *     searchPlaceholder="Search by product, category, code…"
 *   />
 *
 * `columns[i].render` should produce the cell content. `columns[i].search`
 * (optional) is a function returning a string the row search should index on
 * for that column. If omitted, the column is not searched.
 *
 * `filters[i].match(row, value)` returns true if the row passes the filter.
 *
 * Each `<td>` gets a `data-label="<column.label>"` so the existing mobile
 * card-stacker CSS displays a label + value on small screens.
 */
export default function DataList({
  testId,
  rows = [],
  columns = [],
  filters = [],
  rowKey = (r) => r.id,
  rowProps = () => ({}),
  emptyText = "No results.",
  searchPlaceholder = "Search…",
  enableSearch = true,
  initialFilters = {},
}) {
  const [q, setQ] = useState("");
  const [activeFilters, setActiveFilters] = useState(() =>
    Object.fromEntries(filters.map((f) => [f.key, initialFilters[f.key] ?? f.options?.[0]?.value ?? "all"]))
  );

  // Apply filters then search.
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter((r) => {
      for (const f of filters) {
        const v = activeFilters[f.key];
        if (!f.match(r, v)) return false;
      }
      if (!ql) return true;
      for (const c of columns) {
        if (typeof c.search !== "function") continue;
        const txt = c.search(r);
        if (txt && String(txt).toLowerCase().includes(ql)) return true;
      }
      return false;
    });
  }, [rows, q, activeFilters, columns, filters]);

  const showToolbar = enableSearch || filters.length > 0;
  const totalShown = filtered.length;
  const totalAll = rows.length;
  const isFiltered = q.trim() || Object.entries(activeFilters).some(([k, v]) => {
    const f = filters.find((f) => f.key === k);
    return f && v !== (f.options?.[0]?.value ?? "all");
  });

  return (
    <div data-testid={testId} className="data-list">
      {showToolbar && (
        <div className="dl-toolbar" data-testid={testId ? `${testId}-toolbar` : "data-list-toolbar"}>
          {enableSearch && (
            <div className="dl-search">
              <Search size={14} color="var(--ink-mute)" />
              <input
                type="search"
                className="input dl-search-input"
                data-testid={testId ? `${testId}-search` : "data-list-search"}
                placeholder={searchPlaceholder}
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              {q && (
                <button type="button" className="btn btn-ghost dl-search-clear"
                        data-testid={testId ? `${testId}-search-clear` : "data-list-search-clear"}
                        onClick={() => setQ("")} aria-label="Clear search">
                  <X size={14} />
                </button>
              )}
            </div>
          )}
          {filters.map((f) => (
            <div key={f.key} className="dl-filter-group" data-testid={testId ? `${testId}-filter-${f.key}` : `data-list-filter-${f.key}`}>
              <div className="dl-filter-label">
                <FilterIcon size={11} /> {f.label}
              </div>
              <div className="dl-chips">
                {f.options.map((opt) => {
                  const active = activeFilters[f.key] === opt.value;
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      data-testid={testId ? `${testId}-chip-${f.key}-${opt.value}` : `data-list-chip-${f.key}-${opt.value}`}
                      className={`btn dl-chip ${active ? "dl-chip-active" : ""}`}
                      onClick={() => setActiveFilters((cur) => ({ ...cur, [f.key]: opt.value }))}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filter count summary */}
      {isFiltered && (
        <div className="dl-summary" data-testid={testId ? `${testId}-summary` : "data-list-summary"}>
          Showing <b>{totalShown}</b> of {totalAll}
        </div>
      )}

      {/* Table */}
      <div className="card dl-card">
        {totalShown === 0 ? (
          <div className="dl-empty" data-testid={testId ? `${testId}-empty` : "data-list-empty"}>
            {isFiltered ? "No matches for the current filters." : emptyText}
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key} style={c.headerStyle}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={rowKey(r)} {...rowProps(r)}>
                  {columns.map((c) => (
                    <td key={c.key} data-label={c.label} style={c.cellStyle}>
                      {c.render ? c.render(r) : r[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
